import React, { useState, useMemo } from 'react';
import {
    Card, Table, Input, Select, DatePicker, Tag, Tooltip, Space, Button, Alert, Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import useEmailLog from './useEmailLog';
import '../dashboard/Dashboard.css';

const { RangePicker } = DatePicker;
const { Title } = Typography;

// Matches the labels produced by backend lib/email-log.js (shared template ids
// yield combined labels).
const EMAIL_TYPE_OPTIONS = [
    'User Onboarding',
    'Interview Scheduling — Recruiter',
    'Interview Scheduling — Candidate',
    'New Candidate (Apply-by-Text) / Email-Group Routing',
    'Inbound Message',
    'Broadcast Response',
    'Campaign Completion / Generic',
    'Password Reset',
    'Chatbot Ready',
    'Tech Support',
    'SMB Lead Welcome',
    'SMB Lead Preview',
    'SMB Lead Share',
    'Custom Details',
    'other',
].map((v) => ({ label: v, value: v }));

const EmailList = ({ list, fallback = '—' }) => {
    if (!Array.isArray(list) || list.length === 0) return <span style={{ color: '#bfbfbf' }}>{fallback}</span>;
    return (
        <Tooltip title={list.join(', ')}>
            <span>{list.length === 1 ? list[0] : `${list[0]} +${list.length - 1}`}</span>
        </Tooltip>
    );
};

const EmailLog = () => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState();
    const [emailType, setEmailType] = useState();
    const [isTest, setIsTest] = useState();
    const [range, setRange] = useState(null);

    const filters = useMemo(() => ({
        search: search || undefined,
        status,
        email_type: emailType,
        is_test: isTest,
        from: range && range[0] ? range[0].toISOString() : undefined,
        to: range && range[1] ? range[1].toISOString() : undefined,
    }), [search, status, emailType, isTest, range]);

    const { data, loading, error, refetch } = useEmailLog({ page, pageSize, filters });

    const resetToFirst = (setter) => (val) => { setter(val); setPage(1); };

    const columns = [
        {
            title: 'Sent At',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 180,
            render: (t) => (t ? moment(t).format('MMM D, YYYY h:mm:ss A') : ''),
        },
        {
            title: 'Type',
            dataIndex: 'email_type',
            key: 'email_type',
            width: 180,
            ellipsis: true,
        },
        {
            title: 'From',
            dataIndex: 'from_email',
            key: 'from_email',
            width: 170,
            render: (v, r) => (r.from_name ? <Tooltip title={`${r.from_name} <${v}>`}><span>{v}</span></Tooltip> : v),
        },
        { title: 'To', key: 'to', render: (_, r) => <EmailList list={r.to_addrs} /> },
        { title: 'CC', key: 'cc', render: (_, r) => <EmailList list={r.cc_addrs} /> },
        { title: 'BCC', key: 'bcc', render: (_, r) => <EmailList list={r.bcc_addrs} /> },
        {
            title: 'Subject', dataIndex: 'subject', key: 'subject', ellipsis: true,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (s, r) => {
                if (s === 'failed') {
                    return (
                        <Tooltip title={r.error || 'Failed'}>
                            <Tag color="red">failed</Tag>
                        </Tooltip>
                    );
                }
                return <Tag color="green">sent</Tag>;
            },
        },
        {
            title: '',
            dataIndex: 'is_test',
            key: 'is_test',
            width: 60,
            render: (v) => (v ? <Tag color="blue">TEST</Tag> : null),
        },
    ];

    return (
        <div className="gh-users-page" style={{ padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Title level={4} style={{ margin: 0 }}>Email Log</Title>
                <Button icon={<ReloadOutlined />} onClick={refetch}>Refresh</Button>
            </div>

            <Card className="gh-dash-card" bodyStyle={{ paddingTop: 12 }}>
                <Space wrap style={{ marginBottom: 12 }}>
                    <Input.Search
                        placeholder="Search recipient or subject"
                        allowClear
                        style={{ width: 260 }}
                        onSearch={resetToFirst(setSearch)}
                    />
                    <Select
                        placeholder="Type"
                        allowClear
                        style={{ minWidth: 220 }}
                        options={EMAIL_TYPE_OPTIONS}
                        onChange={resetToFirst(setEmailType)}
                    />
                    <Select
                        placeholder="Status"
                        allowClear
                        style={{ minWidth: 120 }}
                        options={[{ label: 'Sent', value: 'sent' }, { label: 'Failed', value: 'failed' }]}
                        onChange={resetToFirst(setStatus)}
                    />
                    <Select
                        placeholder="Test?"
                        allowClear
                        style={{ minWidth: 120 }}
                        options={[{ label: 'Real only', value: false }, { label: 'Test only', value: true }]}
                        onChange={resetToFirst(setIsTest)}
                    />
                    <RangePicker showTime onChange={resetToFirst(setRange)} />
                </Space>

                {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}

                <Table
                    className="gh-dash-interview-table"
                    columns={columns}
                    dataSource={data.rows}
                    loading={loading}
                    rowKey="id"
                    size="small"
                    pagination={{
                        current: data.page,
                        pageSize: data.pageSize,
                        total: data.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['25', '50', '100'],
                        showTotal: (t) => `${t} emails`,
                        onChange: (p, ps) => { setPage(p); setPageSize(ps); },
                    }}
                />
            </Card>
        </div>
    );
};

export default EmailLog;
